import { useCallback, useEffect, useRef } from 'react';

/** How long after the last local edit before the parent's value takes over again. */
export const PARENT_VALUE_SETTLE_MS = 1000;

/**
 * Lets a field keep its own value through a burst of edits, then hands it back to its parent.
 *
 * A field that saves each edit as it happens (a stepper's +/−) gets those saves back from its parent
 * after later edits - and a value can't say which edit it belongs to: the same number can be sent
 * twice, and renders can merge the parent's updates so a value comes back unchanged, or not at all
 * (KAIDEV-4328, KAIDEV-4340). So: while edits keep coming, the parent's value is ignored; once they
 * pause, the parent's current value is shown, whether or not it changed in the meantime.
 *
 * - `showParentValue` is called with the parent's value whenever it should be shown.
 * - `isHeld` keeps the field's own value for longer, e.g. while it's focused or a save is waiting.
 * - Call the returned `markEdited` on every local edit or save, and `showParentValueIfSettled` to
 *   re-check when something `isHeld` depends on changes (e.g. on blur).
 */
export const useParentValueOnceSettled = <T>(
  parentValue: T,
  showParentValue: (value: T) => void,
  isHeld: () => boolean = () => false,
) => {
  const parentValueRef = useRef(parentValue);
  parentValueRef.current = parentValue;
  const showParentValueRef = useRef(showParentValue);
  showParentValueRef.current = showParentValue;
  const isHeldRef = useRef(isHeld);
  isHeldRef.current = isHeld;
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showParentValueIfSettled = useCallback(() => {
    if (settleTimeoutRef.current !== undefined || isHeldRef.current()) return;
    showParentValueRef.current(parentValueRef.current);
  }, []);

  const markEdited = useCallback(() => {
    clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(() => {
      settleTimeoutRef.current = undefined;
      showParentValueIfSettled();
    }, PARENT_VALUE_SETTLE_MS);
  }, [showParentValueIfSettled]);

  useEffect(() => {
    showParentValueIfSettled();
  }, [parentValue, showParentValueIfSettled]);

  useEffect(() => () => clearTimeout(settleTimeoutRef.current), []);

  return { markEdited, showParentValueIfSettled };
};
